import React from "react";
import { Eye, Heart, MessageCircle, Share2, Bookmark, MousePointerClick } from "lucide-react";
import { LatestObservation, NormalizedMetrics, PostAnalytics } from "../models/PostAnalytics";

/**
 * Per-publication engagement metrics line for PostViewer.
 *
 * States:
 *  - collecting: published but no observation yet
 *  - metrics + visible "updated X ago" text
 *  - reconnect required (paused fetching)
 *  - "Analytics coming soon" when the analytics scope isn't granted yet
 *  - hidden entirely when the platform is unsupported / post deleted there
 */
export function analyticsReconnectNeeded(conn?: {
  analyticsAccess?: { state?: string };
}): boolean {
  return conn?.analyticsAccess?.state === "reconnect_required";
}

export function PostMetricsRow({
  analytics,
  reconnectHint,
  connectionUpdatedAt,
}: {
  analytics?: PostAnalytics;
  /** Show the reconnect nudge even though metrics may partially work (FB). */
  reconnectHint?: boolean;
  /** Connection doc's updatedAt — a reconnect bumps it past the doc's last
   *  failed probe, letting stale dead-token nudges resolve instantly. */
  connectionUpdatedAt?: number;
}) {
  if (!analytics) return null; // published before analytics existed & not backfilled

  const { latest, sync } = analytics;

  if (sync.state === "reconnect_required") {
    // Connection touched after the failed probe → user (re)connected; the
    // doc just hasn't re-probed yet.
    if (
      connectionUpdatedAt &&
      sync.lastAttemptAt &&
      connectionUpdatedAt > sync.lastAttemptAt
    ) {
      return <p className="text-[11px] text-gray-400">Collecting metrics…</p>;
    }
    return (
      <p className="text-[11px] text-yellow-600">
        Reconnect this account to resume metrics
      </p>
    );
  }

  if (!latest) {
    // Unsupported platforms complete without ever producing an observation.
    if (sync.state === "complete" || sync.state === "deleted_on_platform") {
      return null;
    }
    if (sync.state === "scope_missing") {
      // remedy === "reconnect": the server now requests the missing scope
      // (post env-flip), so reconnecting actually fixes it. Otherwise the
      // platform approval is still pending — reconnecting would do nothing,
      // so just market the feature.
      if (sync.remedy === "reconnect") {
        // Already reconnected (connection carries the scope, doc just hasn't
        // re-probed yet) → the truthful state is "fetch pending".
        if (reconnectHint === false) {
          return (
            <p className="text-[11px] text-gray-400">Collecting metrics…</p>
          );
        }
        return (
          <p className="text-[11px] text-gray-500">
            Reconnect this account to enable analytics
          </p>
        );
      }
      return <p className="text-[11px] text-gray-400">Analytics coming soon</p>;
    }
    if (reconnectHint) {
      return (
        <p className="text-[11px] text-gray-500">
          Reconnect this account to enable analytics
        </p>
      );
    }
    // The first poll fires 1 hour after publish (tiered schedule) — set that
    // expectation instead of looking stuck. Older posts still awaiting a
    // fetch (backfill, retries) keep the generic line.
    if (Date.now() < analytics.publishedAt + 3_600_000) {
      return (
        <p className="text-[11px] text-gray-400">
          Metrics arrive about an hour after publishing
        </p>
      );
    }
    return <p className="text-[11px] text-gray-400">Collecting metrics…</p>;
  }

  const chips = buildChips(latest.metrics);
  if (chips.length === 0) return null;
  return (
    <>
      <ChipLine chips={chips} observedAt={latest.observedAt} />
      {reconnectHint && (
        <p className="text-[11px] text-gray-500">
          Reconnect this account to see views
        </p>
      )}
    </>
  );
}

/**
 * Aggregate engagement line for one post across all its publications (Posts
 * list rows). Sums each connection's latest observation, resolving the
 * per-platform metric preference (videoViews over impressions, likes over
 * reactions) per connection BEFORE summing so mixed-platform posts total
 * correctly. Renders nothing until at least one publication has an
 * observation — per-publication states (collecting / reconnect / coming
 * soon) stay on the detail surfaces.
 */
export function PostMetricsSummary({ records }: { records?: PostAnalytics[] }) {
  const observed = (records ?? []).filter(
    (r): r is PostAnalytics & { latest: LatestObservation } => !!r.latest
  );
  if (observed.length === 0) return null;

  const total = (
    pick: (m: NormalizedMetrics) => number | null | undefined
  ): number | null => {
    let any = false;
    let sum = 0;
    for (const r of observed) {
      const v = pick(r.latest.metrics);
      if (typeof v === "number") {
        any = true;
        sum += v;
      }
    }
    return any ? sum : null;
  };

  const chips = buildChips({
    videoViews: total((m) => firstNumber(m.videoViews, m.impressions)),
    likes: total((m) => firstNumber(m.likes, m.reactions)),
    comments: total((m) => m.comments),
    shares: total((m) => m.shares),
    saves: total((m) => m.saves),
    outboundClicks: total((m) => firstNumber(m.outboundClicks, m.contentClicks)),
  });
  if (chips.length === 0) return null;

  const newestObservedAt = Math.max(...observed.map((r) => r.latest.observedAt));
  return <ChipLine chips={chips} observedAt={newestObservedAt} />;
}

type Chip = { icon: React.ReactNode; value: number; label: string };

function buildChips(m: NormalizedMetrics): Chip[] {
  const chips: Chip[] = [];
  const views = firstNumber(m.videoViews, m.impressions);
  if (views != null) {
    chips.push({ icon: <Eye size={12} />, value: views, label: "views" });
  }
  const likes = firstNumber(m.likes, m.reactions);
  if (likes != null) {
    chips.push({ icon: <Heart size={12} />, value: likes, label: "likes" });
  }
  if (m.comments != null) {
    chips.push({ icon: <MessageCircle size={12} />, value: m.comments, label: "comments" });
  }
  if (m.shares != null) {
    chips.push({ icon: <Share2 size={12} />, value: m.shares, label: "shares" });
  }
  if (m.saves != null) {
    chips.push({ icon: <Bookmark size={12} />, value: m.saves, label: "saves" });
  }
  const clicks = firstNumber(m.outboundClicks, m.contentClicks);
  if (clicks != null) {
    chips.push({ icon: <MousePointerClick size={12} />, value: clicks, label: "clicks" });
  }
  return chips;
}

function ChipLine({ chips, observedAt }: { chips: Chip[]; observedAt: number }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
      {chips.map((chip) => (
        <span
          key={chip.label}
          className="inline-flex items-center gap-1 text-[11px] text-gray-600"
          aria-label={`${formatCount(chip.value)} ${chip.label}`}
        >
          {chip.icon}
          {formatCount(chip.value)}
        </span>
      ))}
      <span className="text-[11px] text-gray-400">
        Updated {timeAgo(observedAt)}
      </span>
    </div>
  );
}

function firstNumber(...values: Array<number | null | undefined>): number | null {
  for (const v of values) {
    if (typeof v === "number") return v;
  }
  return null;
}

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`;
  return String(n);
}

function timeAgo(timestamp: number): string {
  const diffMs = Date.now() - timestamp;
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
